import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import adminService from '../../services/adminService';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';
const getHeaders = () => ({ ...adminService.getAuthHeaders() });

const nm = {
  bg:          '#e8ecf1',
  shadow:      '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm:    '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
};

// ── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ toast }) => (
  <AnimatePresence>
    {toast && (
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        style={{
          position: 'fixed', bottom: 24, right: 24, padding: '13px 20px', borderRadius: 12,
          fontWeight: 700, fontSize: 13, zIndex: 9999,
          background: toast.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color: toast.type === 'success' ? '#065f46' : '#991b1b',
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        }}>
        {toast.msg}
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Neumorphic card ──────────────────────────────────────────────────────────
const NmCard = ({ children, style }) => (
  <div style={{ background: nm.bg, borderRadius: 18, padding: 28, boxShadow: nm.shadowSm, ...style }}>
    {children}
  </div>
);

// ── Section title ────────────────────────────────────────────────────────────
const SectionTitle = ({ icon, title, sub }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
    <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(124,58,237,0.30)', flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', letterSpacing: -0.3 }}>{title}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>
    </div>
  </div>
);

// ── Markup slider + input ────────────────────────────────────────────────────
function MarkupSetting({ value, onChange, onSave, saving }) {
  const examples = [
    { label: '1 DGCC Kei cost', field: 'kei',  base: 1.00 },
    { label: '0.10 DGCC Kei cost', field: 'img', base: 0.10 },
    { label: '0.50 DGCC Kei cost', field: 'vid', base: 0.50 },
  ];

  const chargedDgcc = (base) => +(base * (1 + value / 100)).toFixed(4);

  return (
    <NmCard>
      <SectionTitle
        icon={<svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
        title="Platform Markup"
        sub="Applied automatically on every generation. Users only see the final DGCC charged - markup is never disclosed."
      />

      {/* Main markup control */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center', marginBottom: 28 }}>
        {/* Slider */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Markup Percentage</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#7c3aed', letterSpacing: -0.5 }}>{value}%</span>
          </div>
          <div style={{ position: 'relative', height: 8, background: nm.bg, borderRadius: 10, boxShadow: nm.shadowInset }}>
            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(value / 3, 100)}%`,
              background: 'linear-gradient(90deg,#7c3aed,#6d28d9)', borderRadius: 10, transition: 'width 0.15s' }} />
          </div>
          <input
            type="range" min={0} max={300} step={5} value={value}
            onChange={e => onChange(Number(e.target.value))}
            style={{ width: '100%', marginTop: 8, accentColor: '#7c3aed', cursor: 'pointer' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>
            <span>0%</span><span>75%</span><span>150%</span><span>225%</span><span>300%</span>
          </div>
        </div>

        {/* Number input */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Set Exact</div>
          <div style={{ display: 'flex', alignItems: 'center', background: nm.bg, borderRadius: 12, boxShadow: nm.shadowInset, overflow: 'hidden' }}>
            <input
              type="number" min={0} max={500} step={1} value={value}
              onChange={e => onChange(Number(e.target.value))}
              style={{ width: 68, textAlign: 'center', background: 'transparent', border: 'none', outline: 'none',
                fontSize: 22, fontWeight: 900, color: '#7c3aed', padding: '10px 0 10px 12px' }}
            />
            <span style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed', paddingRight: 12 }}>%</span>
          </div>
        </div>
      </div>

      {/* Live preview table */}
      <div style={{ background: nm.bg, borderRadius: 14, boxShadow: nm.shadowInset, padding: 16, marginBottom: 22 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>
          Live Preview - What Users Are Charged
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {['Scenario', 'Kei.ai Cost (Base)', 'Your Markup', 'User Charged', 'Your Profit'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '1px solid rgba(124,58,237,0.08)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {examples.map(ex => {
              const charged = chargedDgcc(ex.base);
              const profit  = +(charged - ex.base).toFixed(4);
              return (
                <tr key={ex.field} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '9px 10px', color: '#64748b', fontSize: 11 }}>{ex.label}</td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 12 }}>{ex.base.toFixed(2)}</span>
                    <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 3 }}>DGCC</span>
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ background: 'rgba(124,58,237,0.08)', color: '#7c3aed', padding: '2px 7px', borderRadius: 5, fontWeight: 700, fontSize: 10 }}>+{value}%</span>
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ fontWeight: 900, color: '#7c3aed', fontSize: 13 }}>{charged}</span>
                    <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 3 }}>DGCC</span>
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ background: '#ecfdf5', color: '#065f46', padding: '2px 7px', borderRadius: 5, fontWeight: 700, fontSize: 10 }}>+{profit} DGCC</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 8, fontWeight: 500 }}>
          Formula: User charge = Kei.ai base cost × (1 + {value}/100). Users only ever see "DGCC consumed" - never the base cost or markup.
        </div>
      </div>

      {/* Save button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onSave} disabled={saving}
          style={{ background: saving ? '#d1d5db' : 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff', border: 'none',
            borderRadius: 12, padding: '12px 28px', fontSize: 13, fontWeight: 800, cursor: saving ? 'default' : 'pointer',
            boxShadow: saving ? 'none' : '0 4px 14px rgba(124,58,237,0.30)', transition: 'all 0.15s' }}>
          {saving ? 'Saving…' : 'Save Markup'}
        </button>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>Changes take effect on the next generation</span>
      </div>
    </NmCard>
  );
}

// ── Info card - architecture note ─────────────────────────────────────────────
function ArchNote() {
  const items = [
    { icon: '🔒', text: 'Users see only "X DGCC consumed" - base cost and markup are never exposed in any UI or API response.' },
    { icon: '⚡', text: 'Markup is applied in real-time inside chargeForGeneration() on every generation (Image, Video, Music, Chat).' },
    { icon: '📊', text: 'The Billing dashboard shows Aggregator Cost (what Kei.ai / OpenRouter charged us) and Gross Profit (markup revenue) separately.' },
    { icon: '💡', text: 'Setting markup to 0% charges users exactly what Kei.ai charges us - break-even mode.' },
  ];
  return (
    <NmCard>
      <SectionTitle
        icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
        title="How Markup Works"
        sub="Architecture overview - read only"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
            background: nm.bg, borderRadius: 10, padding: '10px 14px', boxShadow: nm.shadowInset }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
            <span style={{ fontSize: 12, color: '#475569', fontWeight: 500, lineHeight: 1.6 }}>{item.text}</span>
          </div>
        ))}
      </div>
    </NmCard>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export function AdminSettingsView() {
  const [markup,  setMarkup]  = useState(80);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 3500);
  }, []);

  // Load current settings
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API_URL}/api/admin/platform-settings`, { headers: getHeaders() });
        const data = await res.json();
        if (data.settings?.markup_pct !== undefined) {
          setMarkup(parseFloat(data.settings.markup_pct));
        }
      } catch { showToast('Failed to load settings', 'error'); }
      finally { setLoading(false); }
    })();
  }, [showToast]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/platform-settings`, {
        method: 'PUT', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: 'markup_pct', value: String(markup) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      showToast(`Markup set to ${markup}% - saved ✓`, 'success');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>
        Loading settings…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <MarkupSetting value={markup} onChange={setMarkup} onSave={save} saving={saving} />
      <ArchNote />
      <Toast toast={toast} />
    </div>
  );
}
